import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { StatusBadge } from '@/components/common/StatusBadge';
import { DataTable } from '@/components/common/DataTable';
import { agentMeAPI, adminAPI, broadcastPreferencesAPI, teamAPI, api } from '@/lib/api';
import { formatDate, formatDateTime, getRoleDisplayName, getPrimaryRole, getErrorMessage, formatPhone } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  User, Mail, Phone, Hash, Building2, Shield, FolderTree,
  Briefcase, FileText, Clock, Activity, Loader2, CheckCircle,
  XCircle, MapPin, Bell, MessageSquare, Save, Link2
} from 'lucide-react';
import { GoogleCalendarIntegration } from '@/components/integrations/GoogleCalendarIntegration';
import { AgentPhotoUpload } from '@/components/agent/AgentPhotoUpload';

export default function ProfilePage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  useEffect(() => { loadProfile(); }, []);

  const loadProfile = async () => {
    try {
      const res = await agentMeAPI.getProfile();
      setProfile(res.data);
    } catch {
      // Not an agent user — show basic profile
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Approved': return <CheckCircle className="text-emerald-500" size={16} />;
      case 'Declined': case 'Terminated': return <XCircle className="text-red-500" size={16} />;
      default: return <Clock className="text-amber-500" size={16} />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // Non-agent user — show basic profile
  if (!profile) {
    return (
      <div data-testid="profile-page">
        <PageHeader title="My Profile" breadcrumbs={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Profile', href: '/profile' }]} />
        <div className="page-content max-w-2xl space-y-4">
          <Card>
            <CardContent className="pt-6 space-y-6">
              <UserPhotoBlock user={user} />
              <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-100">
                <div><Label className="text-xs text-slate-500">Email</Label><p className="text-sm text-slate-900">{user?.email}</p></div>
                <div><Label className="text-xs text-slate-500">Status</Label><div className="mt-1"><StatusBadge status={user?.status} /></div></div>
              </div>
            </CardContent>
          </Card>
          <GoogleCalendarIntegration />
        </div>
      </div>
    );
  }

  const { agent, hierarchy, appointments, requests, activity } = profile;

  const appointmentColumns = [
    { header: 'Carrier', cell: (row) => <span className="font-medium">{row.carrier_name || 'Unknown'}</span> },
    { header: 'Contract Type', cell: (row) => row.contract_type_name || '-' },
    { header: 'Level', cell: (row) => <Badge variant="outline" className="font-mono">{row.level_name || '-'}</Badge> },
    { header: 'Writing #', cell: (row) => <span className="font-mono font-medium">{row.writing_number || '-'}</span> },
    { header: 'Status', cell: (row) => (
      <div className="flex items-center gap-2">
        {getStatusIcon(row.appointment_status)}
        <StatusBadge status={row.appointment_status} />
      </div>
    )},
    { header: 'Appointed Date', cell: (row) => formatDate(row.appointed_date) || '-' },
  ];

  const requestColumns = [
    { header: 'Carrier', cell: (row) => <span className="font-medium">{row.carrier_name || 'Unknown'}</span> },
    { header: 'Requested Level', cell: (row) => row.level_name || '-' },
    { header: 'Status', cell: (row) => <StatusBadge status={row.current_status} /> },
    { header: 'Submitted', cell: (row) => formatDate(row.created_at) },
    { header: 'Notes', cell: (row) => <span className="text-sm text-slate-500 truncate max-w-[200px] inline-block">{row.notes || '-'}</span> },
  ];

  // Summary per carrier
  const contractSummary = appointments.reduce((acc, appt) => {
    const carrier = appt.carrier_name || 'Unknown';
    if (!acc[carrier]) acc[carrier] = { carrier, statuses: [] };
    acc[carrier].statuses.push(appt.appointment_status);
    return acc;
  }, {});

  return (
    <div data-testid="profile-page">
      <PageHeader 
        title="My Profile"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'My Profile', href: '/profile' }
        ]}
      />

      <div className="page-content">
        {/* ═══ HEADER CARD ═══ */}
        <Card className="mb-6" data-testid="agent-overview-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-6">
                <AgentPhotoUploadWrapper agent={agent} onChanged={loadProfile} />
                <div>
                  <h2 className="text-2xl font-heading font-semibold text-slate-900" data-testid="profile-name">
                    {agent.full_name}
                  </h2>
                  <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <Hash size={14} />
                      <span className="font-mono" data-testid="profile-npn">NPN: {agent.npn}</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <Building2 size={14} />
                      <span data-testid="profile-agency">{hierarchy.agency_name || 'Unknown Agency'}</span>
                    </span>
                    <StatusBadge status={agent.status} />
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                    {agent.email && <span className="flex items-center gap-1"><Mail size={13} /> {agent.email}</span>}
                    {agent.phone && <span className="flex items-center gap-1"><Phone size={13} /> {formatPhone(agent.phone)}</span>}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ═══ TABS ═══ */}
        <Tabs defaultValue="contracts">
          <TabsList className="mb-4">
            <TabsTrigger value="contracts" data-testid="tab-my-contracts">
              <Briefcase size={14} className="mr-1" />
              Carrier Appointments ({appointments.length})
            </TabsTrigger>
            <TabsTrigger value="timeline" data-testid="tab-my-timeline">
              <Activity size={14} className="mr-1" />
              Timeline ({activity.length})
            </TabsTrigger>
            <TabsTrigger value="info" data-testid="tab-my-info">
              <User size={14} className="mr-1" />
              Information
            </TabsTrigger>
            <TabsTrigger value="notifications" data-testid="tab-my-notifications">
              <Bell size={14} className="mr-1" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="integrations" data-testid="tab-my-integrations">
              <Link2 size={14} className="mr-1" />
              Integrations
            </TabsTrigger>
          </TabsList>

          {/* Carrier Appointments Tab */}
          <TabsContent value="contracts">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-heading">My Carrier Appointments</CardTitle>
                <a
                  href="/contracts"
                  className="text-xs text-sky-700 hover:text-sky-900 hover:underline font-medium"
                  data-testid="link-to-contracts-page"
                >
                  See all my contract requests →
                </a>
              </CardHeader>
              <CardContent>
                <DataTable columns={appointmentColumns} data={appointments}
                  emptyTitle="No Carrier Appointments Yet" emptyDescription="Once a carrier appointment is approved and issued it will appear here. To view your pending contract requests, use the Contracts page." />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Timeline Tab */}
          <TabsContent value="timeline">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading">Activity Timeline</CardTitle>
              </CardHeader>
              <CardContent>
                {activity.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">No activity history available.</div>
                ) : (
                  <div className="relative">
                    {activity.map((wf, index) => (
                      <div key={wf.workflow_id || index} className="relative pl-8 pb-6 last:pb-0">
                        {index < activity.length - 1 && <div className="absolute left-[11px] top-6 bottom-0 w-0.5 bg-slate-200" />}
                        <div className={`absolute left-0 top-1 w-6 h-6 rounded-full border-2 bg-white flex items-center justify-center ${
                          wf.status === 'Approved' ? 'border-emerald-500' : wf.status === 'Declined' || wf.status === 'Terminated' ? 'border-red-500' : 'border-slate-300'
                        }`}>
                          {getStatusIcon(wf.status)}
                        </div>
                        <div className="bg-slate-50 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <StatusBadge status={wf.status} />
                            <span className="text-xs text-slate-500">{formatDateTime(wf.changed_at)}</span>
                          </div>
                          <p className="text-sm font-medium text-slate-700">{wf.carrier_name}</p>
                          {wf.comment && <p className="text-sm text-slate-600 mt-1">{wf.comment}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Information Tab */}
          <TabsContent value="info">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Contact Information */}
              <Card data-testid="contact-info-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-heading flex items-center gap-2">
                    <Mail size={16} className="text-slate-400" />
                    Contact Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label className="text-xs text-slate-500">Email</Label><p className="text-sm text-slate-900">{agent.email || '-'}</p></div>
                      <div><Label className="text-xs text-slate-500">Phone</Label><p className="text-sm text-slate-900">{formatPhone(agent.phone) || '-'}</p></div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Address</Label>
                      <p className="text-sm text-slate-900">
                        {agent.address_line1 || '-'}
                        {agent.address_line2 && <><br />{agent.address_line2}</>}
                      </p>
                      <p className="text-sm text-slate-900">{[agent.city, agent.state, agent.zip].filter(Boolean).join(', ') || ''}</p>
                    </div>
                    <div><Label className="text-xs text-slate-500">Resident State</Label><p className="text-sm text-slate-900">{agent.state || '-'}</p></div>
                    <LanguagePreferenceField initialValue={agent.preferred_language} />
                  </div>
                </CardContent>
              </Card>

              {/* Hierarchy */}
              <Card data-testid="hierarchy-info-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-heading flex items-center gap-2">
                    <FolderTree size={16} className="text-slate-400" />
                    Hierarchy / Upline
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-slate-500">My Agency</Label>
                        <p className="text-sm font-medium text-slate-900" data-testid="hierarchy-agency">{hierarchy.agency_name || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Agency Level</Label>
                        <p className="text-sm text-slate-900">{hierarchy.agency_depth != null ? `Level ${hierarchy.agency_depth}` : '-'}</p>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Upline Agency</Label>
                      <p className="text-sm text-slate-900" data-testid="hierarchy-upline">{hierarchy.upline_agency_name || 'Root Agency'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">My Role</Label>
                      <p className="text-sm text-slate-900">{getRoleDisplayName(getPrimaryRole(user))}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Licensing */}
              <Card data-testid="licensing-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-heading flex items-center gap-2">
                    <Shield size={16} className="text-slate-400" />
                    Licensing
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-slate-500">NPN</Label>
                        <p className="font-mono text-sm text-slate-900">{agent.npn}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Resident State</Label>
                        <p className="text-sm text-slate-900">{agent.state || '-'}</p>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">SSN Last 4</Label>
                      <p className="font-mono text-sm text-slate-900">{agent.ssn_last4 ? `***-**-${agent.ssn_last4}` : 'Not provided'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Member Since</Label>
                      <p className="text-sm text-slate-900">{formatDate(agent.created_at)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Contracts Summary */}
              <Card data-testid="contracts-summary-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-heading flex items-center gap-2">
                    <Briefcase size={16} className="text-slate-400" />
                    Contracts Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {appointments.length === 0 ? (
                    <div className="text-center py-6 text-slate-400">
                      <Briefcase className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">No carrier contracts yet</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {Object.values(contractSummary).map(({ carrier, statuses }) => {
                        const primary = statuses.includes('Approved') ? 'Approved' : statuses.includes('Pending') ? 'Pending' : statuses[0];
                        return (
                          <div key={carrier} className="flex items-center justify-between p-2.5 rounded-lg border border-slate-100 bg-slate-50/50">
                            <span className="text-sm font-medium text-slate-800">{carrier}</span>
                            <div className="flex items-center gap-2">
                              {statuses.length > 1 && <Badge variant="secondary" className="text-xs">{statuses.length}</Badge>}
                              <StatusBadge status={primary} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Notification Preferences Tab */}
          <TabsContent value="notifications">
            <NotificationPreferencesSection />
          </TabsContent>

          {/* Integrations Tab */}
          <TabsContent value="integrations">
            <div className="max-w-2xl space-y-4">
              <GoogleCalendarIntegration />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}


function NotificationPreferencesSection() {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    email_enabled: true, in_app_enabled: true,
  });
  // Phase 27.30.6 — In-app broadcast pause. Stored on the user record
  // (`broadcast_emails_paused`) and toggled independently from the
  // per-user notification prefs above; saves immediately so users get
  // one-click control without needing the email link.
  const [broadcastPaused, setBroadcastPaused] = useState(false);
  const [broadcastBusy, setBroadcastBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      adminAPI.getNotifPrefs().then((r) => r.data).catch(() => null),
      broadcastPreferencesAPI.getMe().then((r) => r.data).catch(() => null),
    ]).then(([d, bp]) => {
      if (d) {
        setPrefs(d);
        setForm({
          email_enabled: d.email_enabled !== false,
          in_app_enabled: d.in_app_enabled !== false,
        });
      } else {
        toast.error('Failed to load preferences');
      }
      if (bp) setBroadcastPaused(!!bp.broadcast_emails_paused);
      setLoading(false);
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await adminAPI.updateNotifPrefs(form);
      setPrefs(r.data);
      toast.success('Preferences saved');
    } catch { toast.error('Failed to save preferences'); }
    finally { setSaving(false); }
  };

  const toggleBroadcast = async (next) => {
    setBroadcastBusy(true);
    // Optimistic update — revert on failure.
    setBroadcastPaused(next);
    try {
      await broadcastPreferencesAPI.setMe(next);
      toast.success(next ? 'Broadcasts paused' : 'Broadcasts resumed');
    } catch {
      setBroadcastPaused(!next);
      toast.error('Failed to update broadcast preference');
    } finally { setBroadcastBusy(false); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <Card data-testid="notification-preferences-section">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2"><Bell size={16} /> Notification Preferences</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700">Notification Channels</h3>
          <div className="flex items-center justify-between py-2 border-b border-slate-100">
            <div><p className="text-sm font-medium text-slate-700">Email Notifications</p><p className="text-xs text-slate-400">Receive email alerts</p></div>
            <Switch checked={form.email_enabled} onCheckedChange={v => setForm(p => ({ ...p, email_enabled: v }))} data-testid="pref-email-enabled" />
          </div>
          <div className="flex items-center justify-between py-2 border-b border-slate-100">
            <div><p className="text-sm font-medium text-slate-700">In-App Notifications</p><p className="text-xs text-slate-400">Receive notifications inside the app</p></div>
            <Switch checked={form.in_app_enabled} onCheckedChange={v => setForm(p => ({ ...p, in_app_enabled: v }))} data-testid="pref-in-app-enabled" />
          </div>
        </div>

        {/* Phase 27.30.6 — Broadcast pause (independent of the toggles above,
            saves immediately). */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-700">Broadcast Emails</h3>
          <div className="flex items-start justify-between gap-4 rounded-md border border-slate-200 bg-slate-50 p-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800">Pause event broadcasts</p>
              <p className="text-xs text-slate-500 mt-0.5">
                Stops &quot;new event&quot; announcement emails to your inbox. Individual event invitations, RSVPs and reminders are not affected.
              </p>
            </div>
            <Switch
              checked={broadcastPaused}
              onCheckedChange={toggleBroadcast}
              disabled={broadcastBusy}
              data-testid="pref-broadcast-paused"
              aria-label="Pause event broadcasts"
            />
          </div>
        </div>

        <Button onClick={handleSave} disabled={saving} data-testid="save-notification-prefs-btn">
          {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
          Save Preferences
        </Button>
      </CardContent>
    </Card>
  );
}


function LanguagePreferenceField({ initialValue }) {
  const [lang, setLang] = useState((initialValue || 'en').toLowerCase().slice(0, 2));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleChange = async (next) => {
    const prev = lang;
    setLang(next);
    setSaving(true);
    setSaved(false);
    try {
      await agentMeAPI.updatePreferences({ preferred_language: next });
      setSaved(true);
      toast.success(next === 'es' ? 'Idioma preferido: Español' : 'Preferred language: English');
    } catch {
      setLang(prev);
      toast.error('Could not save language preference');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="language-preference-field">
      <Label className="text-xs text-slate-500 flex items-center gap-1">
        <MessageSquare size={12} /> Preferred Language
      </Label>
      <p className="text-[11px] text-slate-400 mb-1.5">
        Texts &amp; emails (reminders, confirmations, urgent tasks) are sent in this language.
      </p>
      <div className="flex items-center gap-2">
        <select
          data-testid="language-preference-select"
          value={lang}
          disabled={saving}
          onChange={(e) => handleChange(e.target.value)}
          className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white disabled:opacity-60"
        >
          <option value="en">English</option>
          <option value="es">Español</option>
        </select>
        {saving && <Loader2 size={14} className="animate-spin text-slate-400" />}
        {saved && !saving && <CheckCircle size={14} className="text-emerald-500" />}
      </div>
    </div>
  );
}


function AgentPhotoUploadWrapper({ agent, onChanged }) {
  const initials = `${agent.first_name?.charAt(0) || ''}${agent.last_name?.charAt(0) || ''}` || '?';
  // Backend returns a relative /api/agent/{npn}/photo URL. Prefix with
  // REACT_APP_BACKEND_URL so <img src=""> resolves against the API host.
  const backend = process.env.REACT_APP_BACKEND_URL || '';
  const url = agent.photo_url
    ? (agent.photo_url.startsWith('http') ? agent.photo_url : `${backend}${agent.photo_url}`)
    : null;
  return (
    <AgentPhotoUpload
      currentUrl={url}
      fallbackInitial={initials}
      onChanged={onChanged}
    />
  );
}

/**
 * Photo upload block for the non-agent profile branch.
 *
 * Non-agents (admins, managers, KR owners) have `photo_storage_key`
 * stored on the `users` document via `POST /api/team/members/{uid}/photo`.
 * The upload endpoint's `_is_self` guard means anyone can update
 * their OWN photo, no KR role required.
 */
function UserPhotoBlock({ user }) {
  const initial = (user?.name || user?.email || '?').trim().charAt(0).toUpperCase();
  // Bust the browser cache after upload/delete so the fresh photo
  // renders immediately.
  const [version, setVersion] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [hasPhoto, setHasPhoto] = useState(Boolean(user?.photo_url));
  const [blobUrl, setBlobUrl] = useState(null);
  const inputRef = React.useRef(null);

  useEffect(() => {
    setHasPhoto(Boolean(user?.photo_url));
  }, [user?.photo_url]);

  // The team photo stream endpoint requires a Bearer token, so a plain
  // <img src> (which cannot send Authorization) always 403s. Fetch the
  // bytes through the authed axios instance and expose them as a blob URL.
  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    if (!hasPhoto || !user?.user_id) { setBlobUrl(null); return undefined; }
    (async () => {
      try {
        const res = await api.get(`/team/members/${user.user_id}/photo?v=${version}`, { responseType: 'blob' });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setBlobUrl(objectUrl);
      } catch {
        if (!cancelled) { setBlobUrl(null); setHasPhoto(false); }
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [hasPhoto, user?.user_id, version]);

  const displaySrc = blobUrl;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Unsupported file type. Please choose a JPG, PNG, or WebP image.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image is too large. Maximum size is 10 MB.');
      return;
    }
    setUploading(true);
    try {
      await teamAPI.uploadPhoto(user.user_id, file);
      setHasPhoto(true);
      setVersion((v) => v + 1);
      toast.success('Profile photo updated');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove your profile photo? Your initials will show instead.')) return;
    setDeleting(true);
    try {
      await teamAPI.deletePhoto(user.user_id);
      setHasPhoto(false);
      setVersion((v) => v + 1);
      toast.success('Profile photo removed');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not remove photo'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex items-center gap-4" data-testid="user-photo-block">
      <div className="relative w-20 h-20 rounded-full bg-slate-900 text-white overflow-hidden flex items-center justify-center text-2xl font-semibold border-2 border-slate-200 shrink-0">
        {displaySrc ? (
          <img
            src={displaySrc}
            alt="Profile"
            className="w-full h-full object-cover"
            onError={() => setHasPhoto(false)}
            data-testid="user-photo-current"
          />
        ) : (
          <span>{initial}</span>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <Loader2 size={22} className="animate-spin text-white" />
          </div>
        )}
      </div>
      <div className="flex-1">
        <h3 className="font-heading text-xl font-semibold text-slate-900">{user?.name}</h3>
        <p className="text-slate-500 text-sm">{getRoleDisplayName(getPrimaryRole(user))}</p>
        <div className="flex gap-2 mt-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFile}
            className="hidden"
            data-testid="user-photo-input"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={uploading || deleting}
            data-testid="user-photo-upload-btn"
          >
            {hasPhoto ? 'Change photo' : 'Upload photo'}
          </Button>
          {hasPhoto && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRemove}
              disabled={uploading || deleting}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              data-testid="user-photo-remove-btn"
            >
              {deleting ? <Loader2 size={14} className="animate-spin" /> : 'Remove'}
            </Button>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-1.5">
          JPG, PNG or WebP. Max 10 MB. We crop to a square automatically.
        </p>
      </div>
    </div>
  );
}

